import { useOutletContext, useParams } from 'react-router-dom';
import type { AppLayoutContext, RoleEnum, SchoolYearRef } from '~/types';

export type EnrollYear =
  | { status: 'ready'; year: SchoolYearRef }
  | { status: 'loading' }
  | { status: 'unavailable' };

/**
 * The URL's year, once the app has selected it.
 *
 * The app selects a URL year only when the signed-in role has it, and a family
 * has only the years it's enrolling in or enrolled in. A year it can't select
 * is one this family can't see: the enroll pages say there's nothing for it and
 * ask the server for nothing. One it can select but hasn't yet is on its way.
 */
export function useEnrollYear(): EnrollYear {
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const { yearSlug } = useParams();

  const selected = roleContext?.selectedSchoolYear;
  if (selected && selected.slug === yearSlug) return { status: 'ready', year: selected };

  const role = roleContext?.selectedRole as RoleEnum | null | undefined;
  const years = roleContext?.accessibleSchools
    ?.find((accessible) => accessible.school.id === school?.id)
    ?.accessibleSchoolYears ?? [];
  const selectable = !!role && years.some((year) => year.schoolYear.slug === yearSlug && year.roles.includes(role));

  return selectable ? { status: 'loading' } : { status: 'unavailable' };
}
